import { Button } from "@/components/ui/button";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  ExpenseStatus,
  EmployeeProfileStatus,
  User,
  UserRole,
  useOrganizationUserQuery,
  useRecentExpenseActivitiesQuery,
  PublicUser,
  useAdminReportGraphMetricsQuery,
  ExpenseReportAnalyticGroup,
  GraphAnalyticInterval,
} from "@common/utils/graphql";
import { formatDistanceToNow, isValid } from "date-fns";
import { useMemo, useState } from "react";
import { InviteOrEditPersonContent } from "./InviteOrEditPersonContent";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { PieChart } from "@common/components/Chart/PieChart";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import { EmployeeFormSchema } from "../forms/Forms";
import { Body } from "@hypercard/web-components";
import { atom } from "recoil";
import { formatDollars, formatDate } from "@common/components/utils";
import { AttributeKeyValueCell } from "@common/components";
import { getRoleOrEmployeeStatus } from "./utils";

// Inclusive of today and 30 days ago
const now = new Date();
const endDay = new Date(
  now.getFullYear(),
  now.getMonth(),
  now.getDate(),
  23,
  59,
  59,
  999,
);
const startDay = new Date(
  now.getFullYear(),
  now.getMonth(),
  now.getDate() - 30,
  0,
  0,
  0,
  0,
);

interface Props {
  person: User;
}

export const viewPersonManager = atom<User | PublicUser | undefined>({
  key: "viewPersonManager",
  default: undefined,
});

export const ViewPersonContent = ({ person }: Props) => {
  const [isEditMode, setIsEditMode] = useState(false);

  const userId = person.userId;
  const employeeId = person.employee?.employeeId;

  const { loading: metadataLoading, data: metadataDataRaw } =
    useOrganizationUserQuery({
      variables: {
        userId: userId,
      },
    });

  const { data: summaryDataRaw, loading: summaryLoading } =
    useAdminReportGraphMetricsQuery({
      variables: {
        groupedBy: ExpenseReportAnalyticGroup.Category,
        interval: GraphAnalyticInterval.Daily,
        filters: {
          transactedAfter: startDay,
          transactedBefore: endDay,
          employeeId: employeeId ? [employeeId] : undefined,
          expenseStatus: [ExpenseStatus.Approved],
        },
      },
    });

  const { loading: recentActivityLoading, data: recentActivityDataRaw } =
    useRecentExpenseActivitiesQuery({
      variables: {
        employeeId: employeeId!,
      },
    });

  const metadata = useMemo(() => {
    if (
      metadataDataRaw?.organizationUser.__typename !==
      "QueryOrganizationUserSuccess"
    ) {
      return undefined;
    }
    return metadataDataRaw?.organizationUser?.data;
  }, [metadataDataRaw]);

  const summaryData = useMemo(() => {
    if (
      summaryDataRaw?.adminReportGraphMetrics?.__typename !==
      "QueryAdminReportGraphMetricsSuccess"
    ) {
      return undefined;
    }
    return summaryDataRaw.adminReportGraphMetrics.data;
  }, [summaryDataRaw]);

  const totalReimbursed = useMemo(() => {
    if (!summaryData) {
      return 0;
    }
    return summaryData.metrics.reduce((acc, curr) => {
      return acc + curr.values[0].value;
    }, 0);
  }, [summaryData]);

  const recentActivityData = useMemo(() => {
    if (
      recentActivityDataRaw?.recentExpenseActivities.__typename !==
      "QueryRecentExpenseActivitiesSuccess"
    ) {
      return [];
    }
    return recentActivityDataRaw.recentExpenseActivities.data;
  }, [recentActivityDataRaw]);

  const form = useForm<z.infer<typeof EmployeeFormSchema>>({
    resolver: zodResolver(EmployeeFormSchema),
  });

  if (metadataLoading && !metadata) {
    return <FullPageLoader />;
  }

  if (isEditMode) {
    return (
      <InviteOrEditPersonContent
        onBack={() => {
          setIsEditMode(false);
        }}
        mode="edit"
        form={form}
        userId={userId}
      />
    );
  }

  const managerName = !metadata?.employee?.manager
    ? "No manager"
    : `${metadata?.employee?.manager?.firstName} ${metadata?.employee?.manager?.lastName}`;

  return (
    <>
      <SheetHeader>
        <SheetTitle>{metadata?.displayName}</SheetTitle>
        <Body size="sm" weight="regular" className="text-slate">
          {metadata?.employee?.workEmail}
        </Body>
      </SheetHeader>
      <div className="mt-2 overflow-auto px-sheet-gap scrollbar-thin">
        <div className="flex justify-between pb-5">
          <AttributeKeyValueCell
            keyName="Role"
            value={getRoleOrEmployeeStatus(metadata)}
          />
          {isValid(metadata?.employee?.employmentStartDate) && (
            <AttributeKeyValueCell
              keyName="Employee Since"
              value={formatDistanceToNow(
                metadata?.employee?.employmentStartDate,
              )}
            />
          )}
        </div>
        <div className="pb-5">
          <AttributeKeyValueCell
            keyName="Department"
            value={metadata?.employee?.department.name}
          />
        </div>
        <div className="pb-5">
          <AttributeKeyValueCell keyName="Manager" value={managerName} />
        </div>
        {metadata?.employee?.status === EmployeeProfileStatus.Active && (
          <Button
            className="w-full"
            onClick={() => {
              const roles = (metadata?.roles || []).filter(
                (role) => role !== UserRole.Cardholder,
              );
              form.reset({
                firstName: metadata?.firstName,
                lastName: metadata?.lastName,
                workEmail: metadata?.employee?.workEmail,
                departmentId: String(metadata?.employee?.department.id),
                managerEmployeeProfileId:
                  metadata?.employee?.manager?.employee!.employeeId,
                roles,
              });
              setIsEditMode(true);
            }}
          >
            Edit Profile
          </Button>
        )}
        <div className="flex flex-col gap-3 space-x-2 border-b bg-transparent pb-4 pt-2 text-sm" />
        <div className="w-full">
          <Body size="sm" weight="regular" className="pt-5 text-white/70">
            Last 30 Days Spend
          </Body>
          {summaryLoading ? (
            <Body size="md" weight="regular">
              Loading...
            </Body>
          ) : (
            <div>
              <Body size="2xl" weight="regular" className="pt-2">
                {formatDollars(totalReimbursed)}
              </Body>
              {summaryData && summaryData?.metrics.length > 0 && (
                <div className="h-100 flex grow flex-row justify-between pt-[20px]">
                  <PieChart
                    data={summaryData?.metrics.map((exp) => ({
                      name: exp.groupedEntityName,
                      amount: exp.totalValue,
                    }))}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="w-full">
          <Body size="sm" weight="regular" className="pb-2 pt-5 text-white/70">
            Recent Activity
          </Body>
          {recentActivityLoading ? (
            <Body size="md" weight="regular">
              Loading...
            </Body>
          ) : (
            <div>
              {recentActivityData.length === 0 && (
                <Body size="md" weight="regular">
                  No recent activity
                </Body>
              )}
              {recentActivityData.map((d, i) => {
                return (
                  <div
                    className="flex flex-row justify-between pb-4"
                    key={`activity-${i}`}
                  >
                    <div className="flex items-center gap-5">
                      <img
                        src={d.transactionCategoryIconUrl}
                        className="h-4 w-4"
                      />
                      <div className="flex flex-col">
                        <Body size="md" weight="regular">
                          {d.__typename === "Expense" ? d.merchantName : null}{" "}
                          {d.__typename === "StipendExpense"
                            ? d.stipendName
                            : null}
                        </Body>
                        <Body size="sm" weight="regular">
                          {formatDate(d.transactionDate)}
                        </Body>
                      </div>
                    </div>
                    <Body
                      size="md"
                      weight="regular"
                      className="flex items-center"
                    >
                      {formatDollars(d.amount)}
                    </Body>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
